import React, { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { dataMappingAPI } from '../services/api';

interface DataSourceProviderForm {
  dataSourceId: string;
  description: string;
  contactName: string;
  email: string;
  portal: string;
  projectName: string;
  deidentified: string;
  clinicalType: string;
}

interface DataSourceProviderRow {
  id: number;
  dataSourceId: string;
  description: string;
  contactName: string;
  email: string;
  portal: string;
  projectName: string;
  deidentified: string;
  clinicalType: string;
  name?: string;
  contact_name?: string;
  contact_email?: string;
  portal_url?: string;
  project_name?: string;
  clinical_type?: string;
}

const DataSourceMapping: React.FC = () => {
  const [items, setItems] = useState<DataSourceProviderRow[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<DataSourceProviderForm>({
    dataSourceId: '',
    description: '',
    contactName: '',
    email: '',
    portal: '',
    projectName: '',
    deidentified: '',
    clinicalType: '',
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const rows = await dataMappingAPI.getDataSources();
        const mapped: DataSourceProviderRow[] = rows.map((row: any) => ({
          id: row.id,
          dataSourceId: row.name || row.id?.toString() || '',
          description: row.description || '',
          contactName: row.contact_name || '',
          email: row.contact_email || '',
          portal: row.portal_url || '',
          projectName: row.project_name || '',
          deidentified: row.deidentified || '',
          clinicalType: row.clinical_type || '',
          name: row.name,
          contact_name: row.contact_name,
          contact_email: row.contact_email,
          portal_url: row.portal_url,
          project_name: row.project_name,
        }));
        setItems(mapped);
      } catch (error) {
        console.error('Error loading data sources for mapping:', error);
        toast.error('Failed to load data sources from Publication System.');
      }
    };

    loadData();
  }, []);

  const handleChange = (field: keyof DataSourceProviderForm, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Wire to backend / integration service
    const nextId = items.length > 0 ? Math.max(...items.map(i => i.id)) + 1 : 1;
    const newItem: DataSourceProviderRow = { id: nextId, ...form };
    setItems(prev => [...prev, newItem]);
    toast.success('Data Source Provider mapping captured (not yet persisted).');
    setForm({
      dataSourceId: '',
      description: '',
      contactName: '',
      email: '',
      portal: '',
      projectName: '',
      deidentified: '',
      clinicalType: '',
    });
    setShowForm(false);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            Source Data Provider Mapping
          </h1>
          <p className="text-sm text-gray-600">
            View all defined data providers and add new mappings that align with the Publication System&apos;s
            Data Sources (Admin Panel &rarr; Data Sources).
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm(prev => !prev)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--accent)] text-white text-sm font-medium shadow-sm hover:opacity-90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Add Provider</span>
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
            Providers
          </h2>
          <span className="text-xs text-gray-500">
            {items.length} record{items.length === 1 ? '' : 's'}
          </span>
        </div>
        {items.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-gray-500">
            No providers defined yet. Click <span className="font-medium">Add Provider</span> to create your first mapping.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Data_Sources_ID</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Description</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Contact</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Email</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Portal</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Clinical Type</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs text-gray-800">{item.dataSourceId}</td>
                    <td className="px-4 py-2 text-gray-800">{item.description}</td>
                    <td className="px-4 py-2 text-gray-800">{item.contactName}</td>
                    <td className="px-4 py-2 text-gray-700">{item.email}</td>
                    <td className="px-4 py-2 text-blue-600 underline break-all">{item.portal}</td>
                    <td className="px-4 py-2 text-gray-800">{item.clinicalType}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="space-y-6 bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data_Sources_ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.dataSourceId}
              onChange={e => handleChange('dataSourceId', e.target.value)}
              placeholder="D001"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
            />
            <p className="mt-1 text-xs text-gray-500">
              Unique identifier for each data provider (D + 3 digits, e.g., D001, D002).
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Deidentify_or_not
            </label>
            <select
              value={form.deidentified}
              onChange={e => handleChange('deidentified', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] bg-white"
            >
              <option value="">Select...</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              &quot;Yes&quot; if data is deidentified, &quot;No&quot; if contains identifiable information.
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Data_Sources_Description
          </label>
          <input
            type="text"
            value={form.description}
            onChange={e => handleChange('description', e.target.value)}
            placeholder="UCSF | Savic Lab"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
          />
          <p className="mt-1 text-xs text-gray-500">
            Detailed description of the data source and its scope. Use &quot;Institution | Team/Lab&quot; format.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data_Source_Contact_Name
            </label>
            <input
              type="text"
              value={form.contactName}
              onChange={e => handleChange('contactName', e.target.value)}
              placeholder="John Smith"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data_Source_email
            </label>
            <input
              type="email"
              value={form.email}
              onChange={e => handleChange('email', e.target.value)}
              placeholder="name@institution.edu"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data_Source_Portal
            </label>
            <input
              type="url"
              value={form.portal}
              onChange={e => handleChange('portal', e.target.value)}
              placeholder="https://portal.institution.edu"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Clinical_or_Preclinical
            </label>
            <select
              value={form.clinicalType}
              onChange={e => handleChange('clinicalType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] bg-white"
            >
              <option value="">Select...</option>
              <option value="Clinical">Clinical</option>
              <option value="Preclinical">Preclinical</option>
              <option value="Both">Both</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Project_Name
          </label>
          <textarea
            value={form.projectName}
            onChange={e => handleChange('projectName', e.target.value)}
            rows={3}
            placeholder="COVID-19 Drug Response Study; TB PKPD Study"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
          />
          <p className="mt-1 text-xs text-gray-500">
            May contain multiple projects separated by semicolons.
          </p>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setShowForm(false)}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="inline-flex items-center px-4 py-2 rounded-lg bg-[var(--accent)] text-white text-sm font-medium hover:opacity-90 transition-colors"
          >
            Save Mapping
          </button>
        </div>
      </form>
      )}

      <div className="mt-6 text-xs text-gray-500">
        This section is for defining structured mappings between institutional data providers and
        Source Data Discovery. Future iterations will connect these definitions directly to the
        Publication System&apos;s Data Sources configuration and shared metadata services.
      </div>
    </div>
  );
};

export default DataSourceMapping;

